// 9fbef606107a605d69c0edbcd8029e5d
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import useHasMounted from '@tcp/core/src/hooks/useHasMounted';
import { spcTitleCase } from '@tcp/core/src/utils/utils';
import {
  getPriceData,
  getOfferPriceAccessibilityLabel,
} from '@tcp/core/src/components/features/browse/PlpTile/views/plpTileHelpers/priceAndWishlistHelper';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import {
  GLOBAL_COMMON_BROWSE_LABELS,
  globalCommonBrowseLabels,
} from '@tcp/core/src/utils/labels/globalCommonBrowseLabels.labels';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import { GlobalAccessibilityLabels } from '@tcp/core/src/utils/labels/globalAccessibility.labels';
import { PLPLabels } from '@tcp/core/src/utils/labels/browse/PLP.labels';
import style, {
  MerchantTag,
  OfferPrice,
  StrikedThroughPrice,
  PriceWrapper,
  ListPriceWrapper,
} from '@tcp/core/src/components/features/browse/BundleProduct/atoms/Price/styles/Price.style';
import SafeComponent from '@tcp/core/src/components/common/hoc/withErrorBoundary';

const Price = ({
  minListPrice,
  minOfferPrice,
  merchantTag,
  isHideOriginalPrice,
  currencyAttributes,
  currencySymbol,
  keepAlive,
  className,
}) => {
  const outOfStockCaps = getLabelFromCopyOrFromTranslation(
    `${GLOBAL_COMMON_BROWSE_LABELS}.lbl_common_outOfStockCaps`,
    globalCommonBrowseLabels
  );
  const hasMounted = useHasMounted();
  const { offerPrice, listPrice } = useMemo(
    () =>
      getPriceData({
        minListPrice,
        minOfferPrice,
        currencySymbol,
        currencyAttributes,
      }),
    [minListPrice, minOfferPrice, currencySymbol, currencyAttributes]
  );

  const shouldRenderListPrice = hasMounted && listPrice !== offerPrice && !isHideOriginalPrice;

  const accessibilityLabels = getLabelFromCopyOrFromTranslation(
    'global.accessibility',
    GlobalAccessibilityLabels
  );

  const priceLabel = getLabelFromCopyOrFromTranslation('PLP.PLP_sort_filter.lbl_price', PLPLabels);
  const salePriceLabel = renderLabel(accessibilityLabels.lbl_sale_price, 'lbl_sale_price');
  const originalPriceLabel = renderLabel(
    accessibilityLabels.lbl_original_price,
    'lbl_original_price'
  );

  return !keepAlive ? (
    <PriceWrapper className={`${className} price-tag-wrapper`}>
      <OfferPrice className="offer-price-tag">
        <span className="sr-only">
          {`${getOfferPriceAccessibilityLabel({
            shouldRenderListPrice,
            priceLabel,
            salePriceLabel,
          })} ${offerPrice}`}
        </span>
        <span aria-hidden="true">{offerPrice}</span>
      </OfferPrice>
      {shouldRenderListPrice ? (
        <ListPriceWrapper className="strike-price-and-merchant-tag-wrapper">
          <StrikedThroughPrice className="strike-price">
            <span className="sr-only">{`${originalPriceLabel}: ${listPrice}`}</span>
            <span aria-hidden="true">{listPrice}</span>
          </StrikedThroughPrice>
          {merchantTag && <MerchantTag className="merchant-tag">{merchantTag}</MerchantTag>}
        </ListPriceWrapper>
      ) : null}
    </PriceWrapper>
  ) : (
    <PriceWrapper className={`${className} keep-alive-text`}>
      {spcTitleCase(outOfStockCaps)}
    </PriceWrapper>
  );
};

Price.propTypes = {
  minListPrice: PropTypes.number,
  minOfferPrice: PropTypes.number,
  merchantTag: PropTypes.string,
  isHideOriginalPrice: PropTypes.bool,
  currencySymbol: PropTypes.string,
  currencyAttributes: PropTypes.shape({}),
  keepAlive: PropTypes.bool,
  className: PropTypes.string,
};
Price.defaultProps = {
  minListPrice: 0,
  minOfferPrice: 0,
  merchantTag: '',
  isHideOriginalPrice: false,
  currencySymbol: '$',
  currencyAttributes: {},
  keepAlive: false,
  className: '',
};

export default withStyles(SafeComponent(Price), style);
export { Price as PriceVanilla };
